import React from 'react';
import autoBind from 'react-autobind';
import Localization from "../Localization.jsx";
import Config from "../Config.jsx";
import { Modal, SimpleGrid, Card, Text, Group, Button, Box, RingProgress } from '@mantine/core';
import {
  IconDatabase,
  IconCopy,
  IconUserShield,
  IconUser,
  IconCurrentLocation,
  IconEye,
  IconWorld,
  IconMap,
  IconBell,
  IconCloudOff,
  IconCategory,
  IconListNumbers,
  IconBooks,
  IconFolder,
  IconInfoCircle,
  IconDownload,
  IconLayoutGrid,
  IconCloudUpload,
  IconStopwatch,
  IconRoute,
  IconVideo,
  IconShare,
  IconMicrophone,
  IconChecklist,
  IconBellRinging,
  IconMapPin,
  IconCube,
  IconRobot,
} from '@tabler/icons-react';

const iconProps = { size: 40, stroke: 1.5, color: 'var(--mantine-color-gray-6)' };
const PLAN_EXTRA_LOCALE = 'PLAN_EXTRA_LOCALE';

function formatFileSize(bytes) {
  if (bytes == null || isNaN(bytes) || bytes === 0) return '0 B';
  const num = Number(bytes);
  if (num < 1024) return num + ' B';
  if (num < 1024 * 1024) return (num / 1024).toFixed(1) + ' KB';
  if (num < 1024 * 1024 * 1024) return (num / (1024 * 1024)).toFixed(2) + ' MB';
  return (num / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

function formatSubscriptionPeriod(period) {
  if (!period) return '';
  const start = period.date_start || '';
  const expire = period.date_expire || '';

  if (start && expire) {
    return `${start} - ${expire}`;
  }

  return start || expire || '';
}

function RingProgressWithLabel({ value, color, label }) {
  const v = value >= 100 ? 100 : value;
  const isError = color === 'error';
  return (
    <RingProgress
      size={80}
      thickness={6}
      roundCaps
      sections={[{ value: v, color: isError ? 'red' : 'brand' }]}
      label={
        <Text size="xs" ta="center" c={isError ? 'red' : 'dimmed'}>
          {label !== undefined ? label : `${Math.ceil(value)}%`}
        </Text>
      }
    />
  );
}

// Choose an icon that best represents the privilege
function getPrivilegeIcon(privilege) {
  const sourceEn = `${privilege?.privilege_name || ''} ${privilege?.privilege_name_long_en || ''}`.toUpperCase();
  const sourceZh = `${privilege?.privilege_name_long_zh || ''}`;
  const privilegeKey = `${privilege?.privilege_name || ''}`.toUpperCase();

  if (privilegeKey === PLAN_EXTRA_LOCALE) {
    return <IconCopy {...iconProps} />;
  }

  if (privilegeKey === 'AI_NPC_CONTENT_TYPE' || sourceEn.includes('NPC')) {
    return <IconRobot {...iconProps} />;
  }

  if (privilegeKey === 'AI_3D_MODEL' || sourceEn.includes('3D MODEL')) {
    return <IconCube {...iconProps} />;
  }

  // High-priority mappings for specific Chinese labels (unique icons)
  if (sourceZh.includes('離線') && sourceZh.includes('類型')) return <IconLayoutGrid {...iconProps} />; // 離線LOCALE戶外教材類型數量
  if (sourceZh.includes('離線') && (sourceZh.includes('數量') || sourceZh.includes('數'))) return <IconDownload {...iconProps} />; // 離線LOCALE戶外教材數量
  if (sourceZh.includes('高解析度') || (sourceZh.includes('照片') && sourceZh.includes('上傳'))) return <IconCloudUpload {...iconProps} />; // 允許高解析度照片上傳
  if (sourceZh.includes('語音回應時間') || sourceZh.includes('回應時間')) return <IconStopwatch {...iconProps} />; // 允許最大語音回應時間(秒)
  if (sourceZh.includes('路徑記錄')) return <IconRoute {...iconProps} />; // 允許用戶路徑記錄
  if (sourceZh.includes('嵌入') && sourceZh.includes('影片')) return <IconVideo {...iconProps} />; // 允許在LOCALE戶外教材中嵌入影片
  if (sourceZh.includes('答案') && (sourceZh.includes('匯出') || sourceZh.includes('導出'))) return <IconShare {...iconProps} />; // 允許答案匯出
  if (sourceZh.includes('語音轉文字')) return <IconMicrophone {...iconProps} />; // 啟用AI語音轉文字
  if (sourceZh.includes('自動評估')) return <IconChecklist {...iconProps} />; // 啟用AI自動評估
  if (sourceZh.includes('進度通知')) return <IconBellRinging {...iconProps} />; // 啟用AI進度通知
  if (sourceZh.includes('位置通知')) return <IconMapPin {...iconProps} />; // 位置通知 → location pin

  // English/general keyword fallbacks
  if (sourceEn.includes('STORAGE')) return <IconDatabase {...iconProps} />;
  if (sourceEn.includes('COPY')) return <IconCopy {...iconProps} />;
  if (sourceEn.includes('ADMIN')) return <IconUserShield {...iconProps} />;
  if (sourceEn.includes('TEACHER') || sourceEn.includes('ACCOUNT')) return <IconUser {...iconProps} />;
  if (sourceEn.includes('GPS')) return <IconCurrentLocation {...iconProps} />;
  if (sourceEn.includes('LOCATION') && (sourceEn.includes('SHOW') || sourceEn.includes('DISPLAY'))) return <IconEye {...iconProps} />;
  if (sourceEn.includes('WORLD')) return <IconWorld {...iconProps} />;
  if (sourceEn.includes('MAP')) return <IconMap {...iconProps} />;
  if (sourceEn.includes('NOTIFICATION')) return <IconBell {...iconProps} />;
  if (sourceEn.includes('OFFLINE')) return <IconCloudOff {...iconProps} />;
  if (sourceEn.includes('TYPE')) return <IconCategory {...iconProps} />;
  if (sourceEn.includes('NUMBER') || sourceEn.includes('QUANTITY') || sourceEn.includes('AMOUNT')) return <IconListNumbers {...iconProps} />;
  if (sourceEn.includes('LOCALE')) return <IconBooks {...iconProps} />;
  if (sourceEn.includes('PACKAGE') || sourceEn.includes('E-PACK')) return <IconFolder {...iconProps} />;

  return <IconInfoCircle {...iconProps} />;
}

class SubscriptionDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ai3dQuota: null,
      ai3dQuotaFetched: false,
      // Locale slots still REMAINING, from get_epack_quota. `null` = not known
      // yet (in flight, or the request failed) — rendered as a dash rather than
      // as a number we would be guessing at.
      epackQuota: null,
    };
    autoBind(this);
  }

  componentDidUpdate(prevProps) {
    const { open, root } = this.props;
    // Refetch on every OPEN, not once per mount: the dialog is mounted for the
    // whole session by TopBar, and this is a consumption counter — creating or
    // duplicating a locale spends a slot, so a value cached at first open goes
    // stale while the dialog sits there unmounted-but-alive.
    if (open && !prevProps.open) {
      this.fetchEpackQuota();
    }
    if (open && !this.state.ai3dQuotaFetched && root && root.refs && root.refs.API) {
      const schoolId = typeof root.getMySchoolId === 'function' ? root.getMySchoolId() : null;
      if (schoolId) {
        root.refs.API.request(
          "get_ai_3d_quota",
          { school_id: schoolId },
          (res) => {
            const payload = res && res.body ? (res.body.result || res.body) : null;
            this.setState({ ai3dQuota: payload, ai3dQuotaFetched: true });
          },
          () => {
            this.setState({ ai3dQuotaFetched: true });
          }
        );
      } else {
        this.setState({ ai3dQuotaFetched: true });
      }
    }
  }

  /**
   * Remaining locale slots — the same `get_epack_quota` figure the create-locale
   * and duplicate-resource gates already enforce on (LocaleViewerBar,
   * ResourceLibraryPage), so the card cannot disagree with the button.
   */
  fetchEpackQuota() {
    const { root } = this.props;
    const schoolId = (root && typeof root.getMySchoolId === 'function') ? root.getMySchoolId() : null;
    if (!root || !root.refs || !root.refs.API || !schoolId) {
      return;
    }
    this.setState({ epackQuota: null });
    root.refs.API.request(
      "get_epack_quota",
      { school_id: schoolId },
      (res) => {
        const raw = res && res.body && res.body.result ? res.body.result.epack_quotas : null;
        const parsed = parseInt(raw, 10);
        this.setState({ epackQuota: isNaN(parsed) ? null : parsed });
      },
      () => {
        this.setState({ epackQuota: null });
      }
    );
  }

  /**
   * The card's numerator: locale slots the school can still spend, against this
   * plan's MAX_LOCALE_NUMBER. Returns null while the figure is unknown.
   *
   * It is the `school_subscription_eapck_quota` BALANCE, not the live epack
   * count this used to show. The quota is a redemption ledger — creating or
   * duplicating a locale spends a slot permanently and deleting one does not
   * refund it — so the count of rows in `epack` answers a different question
   * entirely. School 1166 read "214 / 15": 214 locales accumulated over the
   * years against a plan granted this week with all 15 of its slots intact.
   *
   * Clamped to the plan's own maximum because the balance is a SUM across the
   * school's active plans, and because an over-granted row (the training school
   * carries 990 against a max of 20) would otherwise print a number larger than
   * the denominator.
   */
  remainingLocaleQuota(maxLocale) {
    const remaining = this.state.epackQuota;
    if (remaining === null || remaining === undefined || !(maxLocale > 0)) {
      return null;
    }
    return Math.max(0, Math.min(maxLocale, remaining));
  }

  /**
   * What the card actually PRINTS: slots this plan has already spent, i.e.
   * `MAX_LOCALE_NUMBER - remaining`. Returns null while the figure is unknown.
   *
   * The card used to print the remaining balance over the same denominator, and
   * that reads backwards to everyone who sees it: a freshly granted 10-slot plan
   * with nothing used showed "10 / 10", which every reader took for a school
   * that had used all ten and was about to be blocked — the truth was the
   * opposite. Consumption over entitlement is how the STORAGE_LIMIT card one
   * tile over already reads, and it is what PHP's
   * `get_privilege_detail_by_school_id` reported as `locale_count` before the
   * FastAPI port.
   *
   * Derived from the balance rather than from the school's live `epack` count,
   * so it keeps every property the balance has: the quota is a redemption
   * ledger, deleting a locale does not refund a slot, and locales created before
   * the plan was granted are not charged to it at all — since 2026-09-09 the
   * backend seeds a new row at the plan's full MAX_LOCALE_NUMBER, so a school
   * with years of locales behind it reads `0 / max` on the day it is granted
   * one. Do NOT "correct" this against the epack count; the two answer
   * different questions.
   */
  usedLocaleQuota(maxLocale) {
    const remaining = this.remainingLocaleQuota(maxLocale);
    if (remaining === null) {
      return null;
    }
    return maxLocale - remaining;
  }

  /**
   * The locale allowance the school actually HOLDS — the card's DENOMINATOR.
   *
   * 0 whenever no subscription is in force: a school that has never been granted
   * a plan, and one whose every plan has expired, have both been granted nothing.
   *
   * The privilege row still arrives carrying 20 for them. With no currently-valid
   * subscription the backend answers with the whole `privilege` CATALOG at its
   * `value_default` — `subscription_service.get_privilege_detail_by_school_id`,
   * the `subscription = 'Standard'` branch — and MAX_LOCALE_NUMBER's catalog
   * default is 20 (privilege id 2). That 20 is the value a NEW plan starts from
   * before an admin overrides it on `subscription_privilege`; it is not an
   * entitlement, and nothing on the enforcement path ever honours it. Printing it
   * told school 184 (五旬節林漢光中學, no `school_subscription` row at all) that it
   * held twenty locale slots, and told a lapsed school "20 / 20" — a plan it no
   * longer has, read as exhausted.
   *
   * `hasActivePlan` is `subscriptionPlan === 'Premium'`, the backend's own word
   * for "a subscription is in force right now", and the same test TopBar's Extra
   * Locale item uses. It is passed in rather than read off the provider so this
   * stays a pure function of its two inputs.
   */
  entitledMaxLocale(privilegeValue, hasActivePlan) {
    if (!hasActivePlan) {
      return 0;
    }
    const max = Number(privilegeValue);
    return isNaN(max) ? 0 : max;
  }

  /**
   * The card's NUMERATOR against that entitlement.
   *
   * Deliberately not `usedLocaleQuota` alone: that answers null for a 0 allowance,
   * which is right when the allowance is UNKNOWN (a missing privilege row, or a
   * backend that sent no MAX_LOCALE_NUMBER at all) and wrong when it is known to
   * be zero. A school entitled to nothing has spent nothing of it, so the card
   * reads `0 / 0` — the same reading the admin School List already gives the same
   * school, and independent of whether the `get_epack_quota` balance has arrived.
   *
   * With no plan in force this never consults the balance, which is what keeps
   * the two lapsed states printing the same thing: `get_epack_quota` answers 0
   * for an expired school but 99999 (unlimited) for one that never subscribed,
   * and those would otherwise render as `0 / 0` and `— / 0`.
   */
  usedEntitledLocale(privilegeValue, hasActivePlan) {
    if (!hasActivePlan) {
      return 0;
    }
    return this.usedLocaleQuota(this.entitledMaxLocale(privilegeValue, hasActivePlan));
  }

  render() {
    const { open, onClose, root } = this.props;

    if (!root || !root.refs || !root.refs.userDataProfile) {
      return null;
    }

    const {
      subscriptionPlan,
      subscriptionPlanNames,
      subscriptionPeriods,
      subscriptionInfo,
      school_storage_used,
    } = root.refs.userDataProfile.state;
    // "Premium" is the backend's own word for "this school has a currently-valid
    // subscription" (get_privilege_detail_by_school_id: date_start <= CURDATE()
    // <= date_expire); anything else means it has never been granted a plan, or
    // every plan it holds has expired. The privilege rows below are the CATALOG
    // DEFAULTS in that case, not entitlements — see entitledMaxLocale.
    const hasActivePlan = subscriptionPlan === 'Premium';
    const subscriptionPeriodText = Array.isArray(subscriptionPeriods)
      ? subscriptionPeriods
        .map((period) => {
          const dateRange = formatSubscriptionPeriod(period);
          if (!dateRange) {
            return '';
          }

          return subscriptionPlanNames && subscriptionPlanNames.length > 1 && period.subscription_name
            ? `${period.subscription_name}: ${dateRange}`
            : dateRange;
        })
        .filter(Boolean)
        .join(', ')
      : '';

    return (
      <Modal
        opened={open}
        onClose={onClose}
        title={<Text fw={600}>{Localization.strings.SUBSCRIPTION_DETAILS}</Text>}
        size="85%"
        centered
        zIndex={2000}
      >
        <Box>
          <Group align="center" mb="lg">
            <Box>
              <Text fw={600} size="lg">
                {Localization.strings.PLAN}: {subscriptionPlan} {subscriptionPlanNames && Array.isArray(subscriptionPlanNames) ? `(${subscriptionPlanNames.join(', ')})` : ''}
              </Text>
              {subscriptionPeriodText && (
                <Text size="sm" c="dimmed" mt={4}>
                  {Localization.strings.SUBSCRIPTION_PERIOD || 'Subscription Period'}: {subscriptionPeriodText}
                </Text>
              )}
            </Box>
            {subscriptionPlan !== 'Premium' && (
              <Button>Upgrade</Button>
            )}
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
            {Array.isArray(subscriptionInfo) && subscriptionInfo.map((privilege, index) => {
              const privKey = `${privilege.privilege_name || ''}`.toUpperCase();
              const is3D = privKey === 'AI_3D_MODEL';
              const isNPC = privKey === 'AI_NPC_CONTENT_TYPE';
              const descZhOverride = is3D
                ? (Localization.strings.HOTSPOT_CONTENT_3D_MODEL_DESCRIPTION || '3D模型')
                : (isNPC ? (Localization.strings.NPC_AVATAR_DESCRIPTION || 'NPC 虛擬角色') : null);
              const descEnOverride = is3D
                ? (Localization.strings.HOTSPOT_CONTENT_3D_MODEL_DESCRIPTION || '3D Model')
                : (isNPC ? (Localization.strings.NPC_AVATAR_DESCRIPTION || 'NPC Avatar') : null);
              const displayDescription = Localization.strings.getLanguage() === 'en'
                ? (descEnOverride || privilege.description_en || privilege.privilege_name_long_en)
                : (descZhOverride || privilege.description_zh || privilege.privilege_name_long_zh);

              return (
                <Card key={index} withBorder padding="md" style={{ height: '100%', minHeight: 180, display: 'flex', flexDirection: 'column' }}>
                  {/* Top visual area: either progress for special cases or a representative icon */}
                  <Group justify="center" mb="xs">
                    {privilege.privilege_name === 'STORAGE_LIMIT' && (() => {
                      const storageUsedBytes = school_storage_used * 1024;
                      const storageLimitBytes = Number(privilege.value) * 1024 * 1024;
                      const percentage = storageLimitBytes > 0 ? (storageUsedBytes / storageLimitBytes) * 100 : 0;
                      const color = percentage > 100 ? 'error' : 'primary';
                      return <RingProgressWithLabel value={percentage} color={color} />;
                    })()}
                    {privilege.privilege_name === 'MAX_LOCALE_NUMBER' && (() => {
                      // The ring reads the same way as the text below it, and as
                      // the STORAGE_LIMIT ring beside it: how much of the
                      // allowance is SPENT. Full and red means the next locale
                      // will be refused.
                      const maxLocale = this.entitledMaxLocale(privilege.value, hasActivePlan);
                      const used = this.usedEntitledLocale(privilege.value, hasActivePlan);
                      if (used === null) {
                        return <RingProgressWithLabel value={0} color="primary" label="—" />;
                      }
                      // A zero allowance divides to Infinity, so name the case:
                      // nothing granted is a FULL ring, not an empty one.
                      const percentage = maxLocale > 0 ? (used / maxLocale) * 100 : 100;
                      const color = used >= maxLocale ? 'error' : 'primary';
                      return <RingProgressWithLabel value={percentage} color={color} />;
                    })()}
                    {privilege.privilege_name !== 'STORAGE_LIMIT' && privilege.privilege_name !== 'MAX_LOCALE_NUMBER' && (
                      getPrivilegeIcon(privilege)
                    )}
                  </Group>

                  {/* Description */}
                  <Text ta="center" mb={4} style={{ wordBreak: 'break-word' }}>
                    {displayDescription}
                  </Text>

                  {/* Bottom status/value area */}
                  <Group justify="center" align="center" mt="auto" style={{ minHeight: 28 }}>
                    {(() => {
                      // Boolean type
                      if (privilege.value_type === '1') {
                        const isSupported = `${privilege.value}` === '1' || `${privilege.value}`.toLowerCase() === 'true';
                        const showQuota = privKey === 'AI_3D_MODEL';
                        const quotaValue = (showQuota && this.state.ai3dQuota && (this.state.ai3dQuota.AI_3D_quotas !== undefined))
                          ? this.state.ai3dQuota.AI_3D_quotas
                          : privilege.value_2;
                        const quotaText = isSupported && showQuota && quotaValue !== undefined && quotaValue !== null
                          ? ` (${Localization.strings.REMAINING_QUOTA || 'Remaining quota'}: ${quotaValue})`
                          : '';
                        return (
                          <Text ta="center" fw={700} c={isSupported ? 'green' : 'red'}>
                            {isSupported ? `${Localization.strings.SUPPORTED}${quotaText}` : Localization.strings.NOT_SUPPORTED}
                          </Text>
                        );
                      }

                      // Numeric or other types
                      if (privilege.privilege_name === 'STORAGE_LIMIT') {
                        const storageUsedBytes = school_storage_used * 1024;
                        const storageLimitBytes = Number(privilege.value) * 1024 * 1024;
                        const percentage = storageLimitBytes > 0 ? (storageUsedBytes / storageLimitBytes) * 100 : 0;
                        const overLimit = percentage > 100;
                        return (
                          <Text ta="center" fw={700} c={overLimit ? 'red' : undefined}>
                            {`${formatFileSize(storageUsedBytes)} / ${(Number(privilege.value) / 1024).toFixed(2)} GB`}
                          </Text>
                        );
                      }

                      if (privilege.privilege_name === 'MAX_LOCALE_NUMBER') {
                        const maxLocale = this.entitledMaxLocale(privilege.value, hasActivePlan);
                        const used = this.usedEntitledLocale(privilege.value, hasActivePlan);
                        // The denominator is the RAW privilege string while a plan
                        // is in force — what the backend sent, un-round-tripped
                        // through Number — and a bare 0 when none is.
                        const total = hasActivePlan ? privilege.value : 0;
                        return (
                          <Text ta="center" fw={700} c={used !== null && used >= maxLocale ? 'red' : undefined}>
                            {`${used === null ? '—' : used} / ${total}`}
                          </Text>
                        );
                      }

                      if (privKey === PLAN_EXTRA_LOCALE) {
                        const template = Localization.strings.PLAN_EXTRA_LOCALE_VALUE || '{0} extra LOCALE(s)';
                        return (
                          <Text ta="center" fw={700}>
                            {template.replace('{0}', privilege.value ?? 0)}
                          </Text>
                        );
                      }

                      return (
                        <Text ta="center" fw={700}>
                          {privilege.value}
                        </Text>
                      );
                    })()}
                  </Group>
                </Card>
              );
            })}
          </SimpleGrid>

          <Group justify="flex-end" mt="lg">
            <Button onClick={onClose}>
              {Localization.strings.CLOSE}
            </Button>
          </Group>
        </Box>
      </Modal>
    );
  }
}

export default SubscriptionDialog;
